import React from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Assets/Icon'
import type { InfoHeaderIcon } from './types'

export const InfoHeader: React.FC<{
  headerText: string
  subHeaderText?: string
  icon?: InfoHeaderIcon
  size?: 'large' | 'medium'
}> = ({ headerText, subHeaderText, icon, size = 'medium' }) => {
  return (
    <WrapperStyled data-header-size={size}>
      {icon && (
        <div data-icon-type={icon}>
          <Icon
            name={iconPathDict[icon]}
            size={size === 'large' ? '3.2rem' : '2.4rem'}
            color='currentColor'
          />
        </div>
      )}

      <div className='info-header__text'>
        <h2>{headerText}</h2>

        {subHeaderText && <div>{subHeaderText}</div>}
      </div>
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

const iconPathDict: Record<InfoHeaderIcon, React.ComponentProps<typeof Icon>['name']> = {
  success: 'LineCheckCircle',
  warning: 'LineAlertTriangle',
  error: 'LineAlertCircle',
  info: 'LineInfoCircle',
}

// ======
// Styles
// ======

const WrapperStyled = styled.header({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: spacing.md,

  '[data-icon-type]': {
    display: 'inline-flex',
    borderRadius: radius.full,
    justifyContent: 'center',
    alignItems: 'center',

    '&[data-icon-type="success"]': {
      color: colors.success[500],
      backgroundColor: colors.success[50],
      animation: 'strokeDash 1.3s ease-in-out 0s both',
      strokeDasharray: '10.2rem',

      '@keyframes strokeDash': {
        from: { strokeDashoffset: '10.2rem' },
        to: { strokeDashoffset: '20.5rem' },
      },
    },

    '&[data-icon-type="warning"]': {
      color: colors.warning[500],
      backgroundColor: colors.warning[50],
    },

    '&[data-icon-type="error"]': {
      color: colors.error[500],
      backgroundColor: colors.error[50],
    },

    '&[data-icon-type="info"]': {
      color: colors.mariner[600],
      backgroundColor: colors.mariner[50],
    },
  },

  '&[data-header-size="medium"]': {
    '[data-icon-type]': { height: '4.8rem', width: '4.8rem' },
  },

  '&[data-header-size="large"]': {
    '[data-icon-type]': { height: '6.4rem', width: '6.4rem' },
  },

  '.info-header__text': {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.smx1,
    textAlign: 'center',
    fontFamily: font.family.primary,

    '> h2': {
      margin: 0,
      padding: 0,
      textShadow: 'none',
      fontSize: font.size.xl2.fontSize,
      letterSpacing: font.size.xl2.letterSpacing,
      lineHeight: font.size.xl2.lineHeight,
      fontWeight: font.weight.bold,
      color: colors.black,
      textTransform: 'unset',
      whiteSpace: 'pre-line',
    },

    '> div': {
      fontSize: font.size.lg.fontSize,
      letterSpacing: font.size.lg.letterSpacing,
      lineHeight: font.size.lg.lineHeight,
      fontWeight: font.weight.regular,
      color: colors.neutral[800],
    },
  },
})
